import { getErrorMessage } from '@/utils/errors';
import requisitionsErrors from '@/utils/errors/Modules/requisitions';

/**
 * Sending a requisition for approval fails backend validation when an approval
 * step's only reviewer source — a rel_user ("Users") requisition custom field —
 * has no users selected on the requisition (core: ApprovalUserGroup validates
 * `approval_users` presence with message `err_approval_reviewers_empty`).
 *
 * The requisitions controller re-raises this as `err_requisition_create_failed`
 * / `err_requisition_update_failed` and surfaces the raw ActiveRecord full
 * message (e.g. "Approval users err_approval_reviewers_empty") in `errors[0]`,
 * so there is no clean error key to map — we detect the signal substring.
 */
const APPROVAL_REVIEWERS_EMPTY_SIGNAL = 'err_approval_reviewers_empty';

const APPROVAL_REVIEWERS_EMPTY_MESSAGE =
  'This requisition cannot be sent for approval yet. One of the approval steps ' +
  'takes its reviewers from a "Users" field that has no one selected. Please ' +
  'select at least one user for those fields, then try again.';

/** True when the error payload carries the empty-approval-reviewers signal. */
const hasEmptyApprovalReviewers = (errorData: unknown): boolean => {
  if (errorData === null || errorData === undefined) return false;
  try {
    return JSON.stringify(errorData).includes(APPROVAL_REVIEWERS_EMPTY_SIGNAL);
  } catch {
    return false;
  }
};

export const createRequisitionErrorHandler = (isCreate: boolean) => {
  return (errorKey: string, _result: any) => {
    const errorConfig = requisitionsErrors[errorKey];
    if (!errorConfig?.message) return null;

    const message = errorConfig.message;

    if (message.includes(' / ')) {
      const parts = message.split(' / ').map((part) => part.trim());
      const operationKeyword = isCreate
        ? ['creation', 'create']
        : ['update', 'updating'];

      const matchingParts = parts.filter((part) =>
        operationKeyword.some((keyword) => part.toLowerCase().includes(keyword))
      );

      if (matchingParts.length > 0) {
        const validationPart = matchingParts.find((part) =>
          part.toLowerCase().includes('mandatory')
        );
        return validationPart || matchingParts[0];
      }

      return isCreate ? parts[0] : parts[1] || parts[0];
    }

    return null;
  };
};

export const getRequisitionErrorMessage = (
  error: any,
  isCreate: boolean = false
) => {
  // Callers pass either the raw axios error or the already-unwrapped response
  // body — unwrap so the approval-reviewers check sees the server payload.
  const errorData = error?.response?.data ?? error;

  if (hasEmptyApprovalReviewers(errorData)) {
    return APPROVAL_REVIEWERS_EMPTY_MESSAGE;
  }

  const customErrorHandler = createRequisitionErrorHandler(isCreate);
  return getErrorMessage(error, 'requisitions', customErrorHandler);
};
